import { useTranslation } from 'react-i18next';
import { display } from '../../lib/format';
import { usePublishJob } from './queries';
import { StatusBadge } from '../../components/badges';
import { ErrorAlert } from '../../components/error-alert';
import { RelativeTime } from '../../components/relative-time';
import { PdLink } from '../../components/pd-link';

/** Field-level task table of one publish job (expanded row content). */
export function PublishJobDetail({ jobId, pdDomain }: { jobId: number; pdDomain: string | null | undefined }) {
  const { t } = useTranslation(['publish', 'common']);
  const detail = usePublishJob(jobId);
  if (detail.isLoading) return <p className="bo-muted">{t('detail.loading')}</p>;
  if (detail.error) return <ErrorAlert error={detail.error} />;
  const tasks = detail.data?.tasks ?? [];
  const backupRunId = detail.data?.job.backupRunId;
  return (
    <div className="bo-sections">
      {backupRunId !== null && backupRunId !== undefined && (
        <p className="bo-hint">{t('detail.gated', { id: backupRunId })}</p>
      )}
      <table className="bo-subtable">
        <thead>
          <tr>
            <th>{t('common:table.org')}</th>
            <th>{t('detail.fields')}</th>
            <th>{t('common:table.status')}</th>
            <th>{t('common:table.executed')}</th>
            <th>{t('common:table.note')}</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => (
            <tr key={task.id}>
              <td className="bo-nowrap">
                <PdLink
                  domain={pdDomain}
                  entity={task.entityType === 'person' ? 'person' : 'organization'}
                  pdId={task.pdId}
                />
              </td>
              <td>
                {task.fields.map((f, i) => {
                  const current =
                    f.currentValue === null || f.currentValue === undefined ? null : display(f.currentValue);
                  return (
                    <div key={i} className="bo-mono">
                      <span className="bo-muted">{f.field}:</span>{' '}
                      <span className={current === null ? 'bo-muted' : undefined} title={f.pdFieldKey}>
                        {current ?? '∅'}
                      </span>
                      {' → '}
                      <strong>{f.value === null ? '∅' : display(f.value)}</strong>
                    </div>
                  );
                })}
                {task.fields.length === 0 && <span className="bo-muted">—</span>}
              </td>
              <td>
                <StatusBadge status={task.status} />
              </td>
              <td className="bo-nowrap">
                <RelativeTime value={task.executedAt} />
              </td>
              <td className={task.status === 'skipped' ? 'bo-muted' : 'bo-cell-error'}>
                {task.lastError && (
                  <span className="bo-clip" title={task.lastError}>
                    {task.lastError}
                  </span>
                )}
              </td>
            </tr>
          ))}
          {tasks.length === 0 && (
            <tr>
              <td colSpan={5} className="bo-muted">
                {t('detail.noTasks')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
